import { forwardRef, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronDown, Hash } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { formatCompetence } from '@alamo/utils';

import { useDebounce } from '../../hooks';
import { cn } from '../../lib/cn';
import type { ZIndex } from '../../tokens/z-index';
import { Button } from '../Button/Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Combobox, type ComboboxOption } from '../Combobox';
import { DateInput } from '../DateInput';
import { MonthYearPanel } from '../MonthYearPicker/MonthYearPanel';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover/Popover';

import { getFilterOptionLabel, setFilterOptionLabel } from './filterOptionLabels';
import type { FilterItemConfig, FilterValue, DateRangeValue, NumberRangeValue } from './types';

interface DataTableFilterItemProps {
  config: FilterItemConfig;
  value: FilterValue;
  onChange: (value: FilterValue) => void;
  /**
   * Z tier dos popovers do filtro (portal-rendered). Passar
   * `Z.DROPDOWN_OVER_MODAL` quando o filtro vive dentro de um Dialog/Sheet.
   */
  overlayZIndex?: ZIndex;
}

export function DataTableFilterItem({
  config,
  value,
  onChange,
  overlayZIndex,
}: DataTableFilterItemProps) {
  switch (config.type) {
    case 'select':
      return (
        <SelectFilter
          config={config}
          value={value as string | null}
          onChange={onChange}
          overlayZIndex={overlayZIndex}
        />
      );
    case 'multi-select':
      return (
        <MultiSelectFilter
          config={config}
          value={value as string[] | null}
          onChange={onChange}
          overlayZIndex={overlayZIndex}
        />
      );
    case 'async-select':
      return (
        <AsyncSelectFilter config={config} value={value as string | null} onChange={onChange} />
      );
    case 'month':
      return (
        <MonthFilter
          config={config}
          value={value as string | null}
          onChange={onChange}
          overlayZIndex={overlayZIndex}
        />
      );
    case 'date':
      return <DateFilter config={config} value={value as string | null} onChange={onChange} />;
    case 'date-range':
      return (
        <DateRangeFilter
          config={config}
          value={value as DateRangeValue | null}
          onChange={onChange}
          overlayZIndex={overlayZIndex}
        />
      );
    case 'number-range':
      return (
        <NumberRangeFilter
          config={config}
          value={value as NumberRangeValue | null}
          onChange={onChange}
          overlayZIndex={overlayZIndex}
        />
      );
    case 'boolean':
      return (
        <BooleanFilter
          config={config}
          value={value as boolean | 'true' | 'false' | null}
          onChange={onChange}
        />
      );
    case 'text':
      return <TextFilter config={config} value={value as string | null} onChange={onChange} />;
    default:
      return null;
  }
}

// ---- Select Filter ----

function SelectFilter({
  config,
  value,
  onChange,
  overlayZIndex,
}: {
  config: FilterItemConfig;
  value: string | null;
  onChange: (value: FilterValue) => void;
  overlayZIndex?: ZIndex;
}) {
  const [open, setOpen] = useState(false);
  const options = config.options ?? [];
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <FilterTriggerButton
          label={config.label}
          hasValue={!!value}
          displayValue={selected?.label}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-80 w-52 overflow-y-auto p-1.5 scrollbar-subtle"
        zIndex={overlayZIndex}
      >
        <div className="space-y-0.5">
          <button
            type="button"
            onClick={() => {
              onChange(null);
              setOpen(false);
            }}
            className={cn(
              'w-full text-left rounded-md px-2.5 py-2 text-sm font-body transition-colors',
              !value
                ? 'bg-primary-50 text-primary-700 font-medium'
                : 'text-slate-500 hover:bg-slate-50',
            )}
          >
            {config.placeholder ?? 'Todos'}
          </button>
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
              className={cn(
                'w-full text-left rounded-md px-2.5 py-2 text-sm font-body transition-colors',
                value === option.value
                  ? 'bg-primary-50 text-primary-700 font-medium'
                  : 'text-slate-700 hover:bg-slate-50',
              )}
            >
              {option.label}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---- Multi-Select Filter ----

function MultiSelectFilter({
  config,
  value,
  onChange,
  overlayZIndex,
}: {
  config: FilterItemConfig;
  value: string[] | null;
  onChange: (value: FilterValue) => void;
  overlayZIndex?: ZIndex;
}) {
  const { t } = useTranslation('common');
  const options = config.options ?? [];
  const selected = value ?? [];

  const toggle = (optionValue: string) => {
    if (selected.includes(optionValue)) {
      const next = selected.filter((v) => v !== optionValue);
      onChange(next.length > 0 ? next : null);
    } else {
      onChange([...selected, optionValue]);
    }
  };

  const displayText = selected.length > 0 ? `${selected.length}` : undefined;

  return (
    <Popover>
      <PopoverTrigger>
        <FilterTriggerButton
          label={config.label}
          hasValue={selected.length > 0}
          displayValue={displayText}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-2" zIndex={overlayZIndex}>
        <div className="space-y-0.5 max-h-60 overflow-y-auto scrollbar-subtle">
          {options.map((option) => (
            <label
              key={option.value}
              className="flex items-center gap-2.5 rounded-md px-2 py-1.5 hover:bg-slate-50 cursor-pointer"
            >
              <Checkbox
                checked={selected.includes(option.value)}
                onChange={() => toggle(option.value)}
              />
              <span className="text-sm font-body text-slate-700 flex-1">{option.label}</span>
              {option.count !== undefined && (
                <span className="text-xs text-slate-400">{option.count}</span>
              )}
            </label>
          ))}
        </div>
        {selected.length > 0 && (
          <div className="mt-2 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={() => onChange(null)}
              className="text-xs text-primary-500 hover:text-primary-600 font-body font-medium"
            >
              {t('table.clear_filters')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// ---- Async Select Filter ----
// Server-side searchable single select. Reuses the global <Combobox> (cmdk):
// typing triggers `optionsQueryFn(search)` via TanStack Query after a
// debounce (default 400ms); the first page loads with an empty search.
// Resolved labels are registered in the shared filter-option-label store so
// the active chips can render names instead of raw ids.

function AsyncSelectFilter({
  config,
  value,
  onChange,
}: {
  config: FilterItemConfig;
  value: string | null;
  onChange: (value: FilterValue) => void;
}) {
  const { t } = useTranslation('common');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, config.searchDebounceMs ?? 400);

  const optionsQuery = useQuery({
    queryKey: [
      ...(config.optionsQueryKey ?? (['data-table-filter-options', config.id] as const)),
      debouncedSearch,
    ],
    queryFn: () => config.optionsQueryFn?.(debouncedSearch) ?? Promise.resolve([]),
    enabled: Boolean(config.optionsQueryFn),
    staleTime: 60 * 1000,
    placeholderData: (previous) => previous,
  });

  const fetched = useMemo(
    () => optionsQuery.data ?? config.options ?? [],
    [optionsQuery.data, config.options],
  );

  useEffect(() => {
    for (const option of fetched) {
      setFilterOptionLabel(config.id, option.value, option.label);
    }
  }, [fetched, config.id]);

  const comboboxOptions = useMemo<ComboboxOption<string>[]>(() => {
    const mapped: ComboboxOption<string>[] = fetched.map((option) => ({
      value: option.value,
      label: option.label,
    }));
    // Keep the selected option visible (with its resolved name) even when it
    // falls outside the current search results.
    if (value && !mapped.some((option) => option.value === value)) {
      mapped.unshift({
        value,
        label: getFilterOptionLabel(config.id, value) ?? t('table.selected_option'),
      });
    }
    return mapped;
  }, [fetched, value, config.id, t]);

  return (
    <Combobox<string>
      options={comboboxOptions}
      value={value}
      onChange={(next) => {
        onChange(next);
        setSearch('');
      }}
      onSearch={setSearch}
      loading={optionsQuery.isFetching}
      placeholder={config.placeholder ?? config.label}
      emptyMessage={t('table.no_results')}
      loadingMessage={t('loading')}
      className="w-56"
    />
  );
}

// ---- Month Filter ----
// Compact competence (YYYY-MM) picker for the toolbar: pill trigger + the
// shared MonthYearPanel. The chip/trigger always display the formatted value
// ("junho/2026") — the emitted FilterValue stays the raw 'YYYY-MM' string.

function MonthFilter({
  config,
  value,
  onChange,
  overlayZIndex,
}: {
  config: FilterItemConfig;
  value: string | null;
  onChange: (value: FilterValue) => void;
  overlayZIndex?: ZIndex;
}) {
  const { t, i18n } = useTranslation('common');
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger>
        <FilterTriggerButton
          label={config.label}
          hasValue={!!value}
          displayValue={value ? formatCompetence(value, i18n.language) : undefined}
          icon={<Calendar className="h-3.5 w-3.5" />}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto" zIndex={overlayZIndex}>
        <MonthYearPanel
          value={value}
          onSelect={(next) => {
            onChange(next);
            setOpen(false);
          }}
          onClear={() => {
            onChange(null);
            setOpen(false);
          }}
          clearLabel={t('table.clear_filters')}
        />
      </PopoverContent>
    </Popover>
  );
}

// ---- Date Filter ----
// Single-date inline filter: o input exibe a máscara BR (DD/MM/AAAA) via
// <DateInput>; o FilterValue emitido permanece a string ISO yyyy-mm-dd.

function DateFilter({
  config,
  value,
  onChange,
}: {
  config: FilterItemConfig;
  value: string | null;
  onChange: (value: FilterValue) => void;
}) {
  return (
    <label className="inline-flex items-center gap-1.5">
      <span className="text-sm font-medium font-body text-slate-600">{config.label}</span>
      <DateInput
        value={value ?? ''}
        onChange={(iso) => onChange(iso ?? null)}
        size="sm"
        className="w-36"
        aria-label={config.label}
      />
    </label>
  );
}

// ---- Date Range Filter ----

function DateRangeFilter({
  config,
  value,
  onChange,
  overlayZIndex,
}: {
  config: FilterItemConfig;
  value: DateRangeValue | null;
  onChange: (value: FilterValue) => void;
  overlayZIndex?: ZIndex;
}) {
  const { t } = useTranslation('common');
  const from = value?.from ?? '';
  const to = value?.to ?? '';
  const hasValue = !!(from || to);

  return (
    <Popover>
      <PopoverTrigger>
        <FilterTriggerButton
          label={config.label}
          hasValue={hasValue}
          icon={<Calendar className="h-3.5 w-3.5" />}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-4" zIndex={overlayZIndex}>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium font-body text-slate-600 mb-1.5 block">De</label>
            <input
              type="date"
              value={from}
              onChange={(e) => onChange({ from: e.target.value || null, to: to || null })}
              className="w-full h-9 px-2.5 text-sm font-body rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium font-body text-slate-600 mb-1.5 block">Até</label>
            <input
              type="date"
              value={to}
              onChange={(e) => onChange({ from: from || null, to: e.target.value || null })}
              className="w-full h-9 px-2.5 text-sm font-body rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {hasValue && (
            <Button variant="ghost" size="sm" onClick={() => onChange(null)} fullWidth>
              {t('table.clear_filters')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---- Number Range Filter ----

function NumberRangeFilter({
  config,
  value,
  onChange,
  overlayZIndex,
}: {
  config: FilterItemConfig;
  value: NumberRangeValue | null;
  onChange: (value: FilterValue) => void;
  overlayZIndex?: ZIndex;
}) {
  const { t } = useTranslation('common');
  const min = value?.min ?? '';
  const max = value?.max ?? '';
  const hasValue = min !== '' || max !== '';

  return (
    <Popover>
      <PopoverTrigger>
        <FilterTriggerButton
          label={config.label}
          hasValue={hasValue}
          icon={<Hash className="h-3.5 w-3.5" />}
        />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-4" zIndex={overlayZIndex}>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium font-body text-slate-600 mb-1.5 block">
              {config.prefix ? `${config.prefix} Min` : 'Min'}
            </label>
            <input
              type="number"
              value={min}
              step={config.step ?? 1}
              onChange={(e) =>
                onChange({
                  min: e.target.value !== '' ? Number(e.target.value) : null,
                  max: max !== '' ? Number(max) : null,
                })
              }
              placeholder={config.min !== undefined ? String(config.min) : ''}
              className="w-full h-9 px-2.5 text-sm font-body rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="text-xs font-medium font-body text-slate-600 mb-1.5 block">
              {config.prefix ? `${config.prefix} Max` : 'Max'}
            </label>
            <input
              type="number"
              value={max}
              step={config.step ?? 1}
              onChange={(e) =>
                onChange({
                  min: min !== '' ? Number(min) : null,
                  max: e.target.value !== '' ? Number(e.target.value) : null,
                })
              }
              placeholder={config.max !== undefined ? String(config.max) : ''}
              className="w-full h-9 px-2.5 text-sm font-body rounded-lg border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          {hasValue && (
            <Button variant="ghost" size="sm" onClick={() => onChange(null)} fullWidth>
              {t('table.clear_filters')}
            </Button>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---- Boolean Filter ----
// Accepts boolean and serialized boolean inputs. onChange always emits a real
// boolean (or null), which is what serializes to `field=true`.
function BooleanFilter({
  config,
  value,
  onChange,
}: {
  config: FilterItemConfig;
  value: boolean | 'true' | 'false' | null;
  onChange: (value: FilterValue) => void;
}) {
  const normalized =
    value === 'true' ? true : value === 'false' ? false : (value as boolean | null);

  return (
    <div className="inline-flex w-full items-stretch gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 @[640px]:h-9 @[640px]:w-auto @[640px]:p-0.5">
      <BooleanPill label="Todos" active={normalized === null} onClick={() => onChange(null)} />
      <BooleanPill
        label={config.options?.[0]?.label ?? 'Sim'}
        active={normalized === true}
        onClick={() => onChange(true)}
      />
      <BooleanPill
        label={config.options?.[1]?.label ?? 'Não'}
        active={normalized === false}
        onClick={() => onChange(false)}
      />
    </div>
  );
}

function BooleanPill({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex min-h-11 flex-1 items-center justify-center px-3 @[640px]:min-h-0 @[640px]:flex-initial',
        'rounded-md text-sm font-medium font-body transition-all @[640px]:text-xs',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
        active
          ? 'bg-white text-secondary-950 shadow-sm'
          : 'text-slate-600 hover:text-secondary-950',
      )}
    >
      {label}
    </button>
  );
}

// ---- Text Filter ----

function TextFilter({
  config,
  value,
  onChange,
}: {
  config: FilterItemConfig;
  value: string | null;
  onChange: (value: FilterValue) => void;
}) {
  return (
    <input
      type="text"
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      placeholder={config.placeholder ?? config.label}
      className={cn(
        'h-9 w-40 rounded-lg border border-slate-300 bg-white px-3 text-sm font-body text-slate-700',
        'placeholder:text-slate-400',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
      )}
    />
  );
}

// ---- Shared Trigger Button ----

const FilterTriggerButton = forwardRef<
  HTMLButtonElement,
  {
    label: string;
    hasValue: boolean;
    displayValue?: string;
    icon?: React.ReactNode;
  }
>(function FilterTriggerButton({ label, hasValue, displayValue, icon, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(
        'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium font-body transition-colors',
        hasValue
          ? 'border-primary-200 bg-primary-50 text-primary-700'
          : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:border-slate-300',
      )}
      {...rest}
    >
      {icon}
      <span>{label}</span>
      {displayValue && (
        <>
          <span className="text-slate-300 mx-0.5">|</span>
          <span className="font-semibold">{displayValue}</span>
        </>
      )}
      <ChevronDown className="h-3.5 w-3.5 ml-0.5 text-slate-400" />
    </button>
  );
});
